// src/components/AudioRecorder/FloatingRecorder.js
import React, { useState } from 'react';
import {
  Fab,
  Paper,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  keyframes,
} from '@mui/material';
import {
  Mic as MicIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Close as CloseIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { useRecorder } from '../../contexts/RecorderContext';

// Pulsing animation for recording indicator
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`;

// eslint-disable-next-line max-lines-per-function
const FloatingRecorder = () => {
  const {
    isRecording,
    isPaused,
    duration,
    volumeLevel,
    isExpanded,
    toggleExpanded,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
    showMetadataModal,
  } = useRecorder();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Don't render if not recording and metadata modal isn't open
  if (!isRecording && !showMetadataModal) {
    return null;
  }

  // Don't render the floating recorder when metadata modal is showing
  // (the modal handles post-recording)
  if (showMetadataModal) {
    return null;
  }

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    cancelRecording();
  };

  const handleStopClick = () => {
    stopRecording();
  };

  const handlePauseToggle = () => {
    if (isPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  };

  return (
    <>
      {/* Collapsed State - FAB with timer */}
      <Collapse in={!isExpanded} unmountOnExit>
        <Fab
          color="error"
          onClick={toggleExpanded}
          sx={{
            position: 'fixed',
            bottom: 96, // Above ChatBot FAB (which is at bottom: 24)
            right: 24,
            zIndex: 1350,
            animation: `${pulse} 1.5s infinite`,
            display: 'flex',
            flexDirection: 'column',
            width: 'auto',
            height: 'auto',
            minWidth: 56,
            minHeight: 56,
            padding: '8px 16px',
            borderRadius: '28px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MicIcon />
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {duration}
            </Typography>
          </Box>
        </Fab>
      </Collapse>

      {/* Expanded State - Control Panel */}
      <Collapse in={isExpanded} unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            zIndex: 1400,
            width: 280,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              bgcolor: 'error.main',
              color: 'error.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MicIcon fontSize="small" />
              <Typography variant="subtitle2">
                {isPaused ? 'Paused' : 'Recording'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={toggleExpanded}
              sx={{ color: 'inherit' }}
            >
              <CollapseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            {/* Volume Level Indicator */}
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={isPaused ? 0 : volumeLevel * 100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: volumeLevel > 0.7
                      ? 'error.main'
                      : volumeLevel > 0.3
                        ? 'warning.main'
                        : 'success.main',
                    transition: 'width 0.1s linear',
                  },
                }}
              />
            </Box>

            {/* Timer Display */}
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 500,
                mb: 2,
                color: isPaused ? 'text.secondary' : 'text.primary',
              }}
            >
              {duration}
            </Typography>

            {/* Control Buttons */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {/* Pause/Resume */}
              <IconButton
                onClick={handlePauseToggle}
                color="primary"
                size="large"
                sx={{
                  bgcolor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.main', color: 'white' },
                }}
              >
                {isPaused ? <PlayIcon /> : <PauseIcon />}
              </IconButton>

              {/* Stop */}
              <IconButton
                onClick={handleStopClick}
                color="error"
                size="large"
                sx={{
                  bgcolor: 'error.light',
                  '&:hover': { bgcolor: 'error.main', color: 'white' },
                }}
              >
                <StopIcon />
              </IconButton>

              {/* Cancel */}
              <IconButton
                onClick={handleCancelClick}
                color="default"
                size="large"
                sx={{
                  bgcolor: 'grey.200',
                  '&:hover': { bgcolor: 'grey.400' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Status Text */}
            <Typography
              variant="caption"
              align="center"
              display="block"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              {isPaused ? 'Tap play to resume' : 'Tap collapse to minimize'}
            </Typography>
          </Box>
        </Paper>
      </Collapse>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
      >
        <DialogTitle>Cancel Recording?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this recording? The audio will not be saved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)}>
            Keep Recording
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            Cancel Recording
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FloatingRecorder;
